/**
 * THE START-GATE POLICY — over the pure `startGate` (src/dependencies.ts).
 *
 * `startGate` answers only the fact: is this card waiting on a predecessor that
 * is not ready, and which ones. This module is the POLICY, reconciled with Vinnie
 * 3 Nov 2026:
 *
 *   · SOFT (the default, every board) — a dependency is NOT a block. Starting a
 *     card whose predecessor isn't ready is allowed with NO prompt and NO recorded
 *     override; the card's "Depends on" section shows a calm, dismissible note and
 *     that is the whole of it. (This is the change FROM the old nagging soft-gate,
 *     which stopped to warn and logged an override on every early start.)
 *   · HARD (only where a board admin turned on `depHardBlock`) — a true block: an
 *     acknowledge-only alert, and the start does not happen.
 *
 * It is consulted only at the deliberate-start surfaces (dragging a card on the
 * Board, the card-modal status control, the card-feed status post); a status set
 * in passing elsewhere calls `moveIssue` straight, by design.
 */
import type { IssueStatus } from './types';
import { startGate, type DepCard } from './dependencies';
import { confirmDialog } from './confirm';

/** The columns that mean work has BEGUN. Moving INTO one of these is a start. */
const STARTED = new Set<IssueStatus>(['inprogress', 'staging', 'inreview']);
/** The columns that mean work has NOT begun. Moving OUT of one of these is a start. */
const UNSTARTED = new Set<IssueStatus>(['backlog', 'todo']);

/**
 * A move FROM a not-yet-started column INTO a started one — the only kind of move
 * the gate watches. A card already in flight, moved back, or sent to done/wontdo
 * is not "being started", so it passes through with no question.
 */
export function isDeliberateStart(from: IssueStatus, to: IssueStatus): boolean {
  return UNSTARTED.has(from) && STARTED.has(to);
}

/** "#A", "#A and #B", "#A, #B and #C" — the open predecessors, read as a list. */
function listRefs(refs: string[]): string {
  if (refs.length <= 1) return refs[0] ?? '';
  if (refs.length === 2) return `${refs[0]} and ${refs[1]}`;
  return `${refs.slice(0, -1).join(', ')} and ${refs[refs.length - 1]}`;
}

/**
 * Ask the soft start-gate before a deliberate start. Resolves:
 *   · `{ proceed: true }` — not a start, or nothing is still open: move as normal,
 *     no prompt shown.
 *   · `{ proceed: false }` — blocked, and the person chose Cancel: do NOT move.
 *   · `{ proceed: true, overrideNote }` — blocked, and the person chose "Start
 *     anyway": move, and record `overrideNote` on the card's history.
 *
 * `card` is this card (an Issue satisfies DepCard structurally); `byRef` maps
 * every board card's ref to the card, so the gate can read each predecessor's
 * status; `who` is the person's name, for the override note.
 */
export async function askStartGate(
  card: DepCard,
  status: IssueStatus,
  byRef: Map<string, DepCard>,
  _who: string,
  hard = false,
): Promise<{ proceed: boolean; overrideNote?: string }> {
  // SOFT (default): a dependency never gates a start. No prompt, no override — the
  // card's "Depends on" note is the only signal. Only an admin-enforced board asks.
  if (!hard) return { proceed: true };
  if (!isDeliberateStart(card.status, status)) return { proceed: true };
  const gate = startGate(card, byRef);
  if (!gate.blocked) return { proceed: true };
  const open = listRefs(gate.openPredecessors);
  const plural = gate.openPredecessors.length > 1;
  // HARD — this board's admin has set dependencies to a true block (depHardBlock).
  // There is no override: an acknowledge-only alert, and the move does not happen.
  await confirmDialog({
    title: 'Cannot start this yet',
    body: `This is waiting on ${open}, which ${plural ? 'are' : 'is'} not ready yet. This board does not allow a card to start before the work it waits on is ready.`,
    confirmLabel: 'OK',
    tone: 'caution',
    alert: true,
  });
  return { proceed: false };
}
